// @spec SCF-004
// @spec SCF-005
// @spec SCF-008
import { useAuth, useUser } from '@clerk/clerk-react';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';

import { Providers } from '@/components/providers';
import { createAppRouter } from '@/lib/router';
import type { AuthContext, AuthUser } from '@/types/router';
import './fonts/geist-sans.css';
import '../../../design/theme.css';
import './index.css';

/**
 * Inner app component that uses Clerk hooks to provide auth context to router.
 * Must be rendered inside ClerkProvider (from __root.tsx).
 */
function InnerApp() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();

  // Create router instance once
  const router = useMemo(() => createAppRouter(), []);

  // Build auth context from Clerk state
  const authContext: AuthContext = useMemo(() => {
    const authUser: AuthUser | null = user
      ? {
          id: user.id,
          fullName: user.fullName,
          email: user.primaryEmailAddress?.emailAddress ?? null,
          imageUrl: user.imageUrl,
        }
      : null;

    return {
      isLoaded,
      isAuthenticated: isSignedIn ?? false,
      user: authUser,
      getToken,
    };
  }, [isLoaded, isSignedIn, user, getToken]);

  return <RouterProvider router={router} context={{ auth: authContext }} />;
}

function renderApp(root: ReturnType<typeof createRoot>) {
  root.render(
    <StrictMode>
      <Providers>
        <InnerApp />
      </Providers>
    </StrictMode>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

// Reuse the React root during HMR to avoid ClerkProvider re-initialization errors
const hotData = import.meta.hot?.data as { root?: ReturnType<typeof createRoot> } | undefined;
const root =
  hotData?.root ?? createRoot(rootElement);

if (import.meta.hot) {
  const data = import.meta.hot.data as { root?: ReturnType<typeof createRoot> };
  data.root = root;
  import.meta.hot.dispose(() => {
    // Keep root alive across HMR — disposed only on full page reload
  });
}

renderApp(root);
